import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronDown } from "lucide-react";
import { api, formatApiError, trackEvent } from "@/lib/apiClient";
import { useSEO } from "@/components/Layout";
import { Button } from "@/components/primitives";
import { LeadForm } from "@/components/forms";

function getUTM() {
  const p = new URLSearchParams(window.location.search);
  return { utm_source: p.get("utm_source") || "", utm_medium: p.get("utm_medium") || "", utm_campaign: p.get("utm_campaign") || "" };
}

export default function LandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/landing-pages/${slug}`).then((r) => {
      setPage(r.data);
      trackEvent("landing_page_view", { slug });
    }).catch(() => setNotFound(true));
  }, [slug]);

  useSEO({
    title: page ? (page.seo_title || page.hero_headline) : "Landing",
    description: page?.seo_description || page?.hero_subheadline,
    image: page?.social_image || page?.hero_image,
  });

  if (notFound) return <div className="min-h-screen flex items-center justify-center"><div className="text-center"><h1 className="font-serif-display text-3xl">Page not found</h1><Button to="/" className="mt-6">Home</Button></div></div>;
  if (!page) return <div className="min-h-screen bg-slate-50 animate-pulse" />;

  const s = page.sections || {};
  const c = page.content || {};

  return (
    <div className="bg-white min-h-screen">
      {/* Minimal brand bar */}
      <header className="border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center">
          <Link to="/" className="font-serif-display text-lg font-bold text-[#111827]">IMPACTFUL <span className="text-[#0F2C59]">MINDSET</span></Link>
        </div>
      </header>

      {/* HERO */}
      {s.hero && (
        <section className="border-b border-slate-200">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-serif-display text-4xl sm:text-5xl font-bold leading-tight text-[#111827]">{page.hero_headline}</h1>
              <p className="text-lg text-slate-600 mt-6 leading-relaxed">{page.hero_subheadline}</p>
              {s.primary_cta && <Button as="a" href={page.cta_destination || "#lead-form"} className="mt-8" data-testid="lp-hero-cta">{page.cta_text}</Button>}
            </div>
            {s.product_image && page.hero_image && (
              <div className="aspect-[4/3] bg-slate-100 border border-slate-200 overflow-hidden">
                <img src={page.hero_image} alt={page.hero_headline} className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </section>
      )}

      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        {/* PROBLEM */}
        {s.problem && c.problem_title && (
          <section className="py-14 border-b border-slate-200">
            <span className="overline">The Problem</span>
            <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold mt-3 text-[#111827]">{c.problem_title}</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">{c.problem_body}</p>
          </section>
        )}
        {/* VALUE PROP */}
        {s.value_proposition && c.value_proposition && (
          <section className="py-14 border-b border-slate-200">
            <p className="font-serif-display text-2xl text-[#0F2C59] leading-snug">{c.value_proposition}</p>
          </section>
        )}
        {/* BENEFITS */}
        {s.benefits && c.benefits?.length > 0 && (
          <section className="py-14 border-b border-slate-200">
            <span className="overline">What You'll Gain</span>
            <ul className="mt-5 space-y-3">
              {c.benefits.map((b) => <li key={b} className="flex gap-3 text-slate-700"><Check className="text-[#0F2C59] shrink-0 mt-0.5" size={18} /> <span>{b}</span></li>)}
            </ul>
          </section>
        )}
        {/* FEATURES */}
        {s.features && c.features?.length > 0 && (
          <section className="py-14 border-b border-slate-200">
            <span className="overline">What's Inside</span>
            <div className="grid sm:grid-cols-3 gap-4 mt-5">
              {c.features.map((f) => <div key={f} className="border border-slate-200 p-5 text-slate-700 text-sm">{f}</div>)}
            </div>
          </section>
        )}
        {/* WHO */}
        {s.who_its_for && c.who_its_for && (
          <section className="py-14 border-b border-slate-200">
            <span className="overline">Who It's For</span>
            <p className="text-slate-600 mt-4 leading-relaxed">{c.who_its_for}</p>
          </section>
        )}
        {/* INCLUDED */}
        {s.whats_included && c.whats_included?.length > 0 && (
          <section className="py-14 border-b border-slate-200">
            <span className="overline">What's Included</span>
            <ul className="mt-5 space-y-2">
              {c.whats_included.map((i) => <li key={i} className="flex gap-3 text-slate-700"><Check className="text-[#0F2C59] shrink-0 mt-0.5" size={16} /> {i}</li>)}
            </ul>
          </section>
        )}
        {/* LEAD FORM */}
        {s.lead_form && (
          <section id="lead-form" className="py-14 border-b border-slate-200">
            <div className="border border-slate-200 p-8 bg-[#F8F9FA]">
              <h2 className="font-serif-display text-2xl font-semibold text-[#111827]">{page.cta_text || "Get Instant Access"}</h2>
              <p className="text-slate-600 mt-2 text-sm">{page.lead_form?.consent_text}</p>
              <div className="mt-5">
                <LeadForm resourceSlug={page.lead_form?.resource || ""} campaign={page.campaign_name || slug} source="landing_page" testidPrefix="lp-lead" />
              </div>
            </div>
          </section>
        )}
        {/* AUTHOR */}
        {s.author && c.author && (
          <section className="py-14 border-b border-slate-200">
            <span className="overline">About the Author</span>
            <p className="text-slate-600 mt-4 leading-relaxed">{c.author}</p>
          </section>
        )}
        {/* FAQ */}
        {s.faq && c.faq?.length > 0 && (
          <section className="py-14 border-b border-slate-200">
            <span className="overline">FAQ</span>
            <div className="mt-5 divide-y divide-slate-200 border-t border-slate-200">
              {c.faq.map((f, i) => (
                <div key={i} className="py-4">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex justify-between text-left">
                    <span className="font-semibold text-[#111827]">{f.q}</span>
                    <ChevronDown className={`text-[#0F2C59] transition-transform ${openFaq === i ? "rotate-180" : ""}`} size={18} />
                  </button>
                  {openFaq === i && <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>}
                </div>
              ))}
            </div>
          </section>
        )}
        {/* FINAL CTA */}
        {s.final_cta && (
          <section className="py-16 text-center">
            <h2 className="font-serif-display text-3xl font-semibold text-[#111827]">{c.final_cta_title || "Ready to begin?"}</h2>
            <Button as="a" href="#lead-form" className="mt-6" data-testid="lp-final-cta">{page.cta_text}</Button>
          </section>
        )}
      </div>

      {s.footer && (
        <footer className="bg-[#0b1c38] text-slate-300 py-10 text-center">
          <p className="font-serif-display text-white">IMPACTFUL MINDSET</p>
          <p className="text-xs text-slate-400 mt-2">© {new Date().getFullYear()} Impactful Mindset.</p>
        </footer>
      )}
    </div>
  );
}
